import { useMemo } from "react";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";
import { type QuickSelectOption } from "@features/metrics-explorer/types";
import { useDesignElementManagerV2 } from "@hooks/use-design-element-manager";

export const useQuickSelectOptions = () => {
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const { topSpendPlatforms } = useMetricsExplorerContext();
  const { designElementManager } = useDesignElementManagerV2();

  return useMemo(() => {
    const platformLabels = breakdownConfigs.find(
      (e) => e.key === "Platform (Northbeam)",
    );

    const result: QuickSelectOption[] = [
      {
        title: (
          <>
            {designElementManager.preset.getComponent()}
            Revenue (1st time)
            <span className="text-sm text-v2-action mx-1">&</span>
            {designElementManager.preset.getComponent()}
            Spend by Platform
          </>
        ),
        metrics: [
          {
            metricId: "revFt",
            name: "Revenue (1st time)",
            filters: [],
          },
          ...topSpendPlatforms.map((platform) => ({
            metricId: "spend",
            name: `Spend: ${platform}`,
            filters: [{ breakdown: "Platform (Northbeam)", value: platform }],
          })),
        ],
      },
      {
        title: (
          <>
            {designElementManager.facebook.getComponent()}
            Facebook Spend{" "}
            <span className="text-sm text-v2-action mx-1">&</span>
            {designElementManager.google.getComponent()}
            Google Branded Search Revenue
          </>
        ),
        metrics: [
          {
            name: "Facebook Spend",
            metricId: "spend",
            filters: [
              { breakdown: "Platform (Northbeam)", value: "Facebook Ads" },
            ],
          },
          {
            metricId: "rev",
            name: "Google Branded Search Revenue",
            filters: [
              { breakdown: "Platform (Northbeam)", value: "Google Ads" },
              {
                breakdown: "Category (Northbeam)",
                value: "Paid - Branded Search",
              },
            ],
          },
        ],
      },
      {
        title: (
          <>
            {designElementManager.affiliates.getComponent()}
            Affiliate Spend{" "}
            <span className="text-sm text-v2-action mx-1">&</span>
            {designElementManager.preset.getComponent()}
            Revenue by Source
          </>
        ),
        metrics: [
          {
            metricId: "spend",
            name: "Affiliate Spend",
            filters: [
              { breakdown: "Category (Northbeam)", value: "Affiliate" },
            ],
          },
          {
            metricId: "rev",
            name: "Revenue: Amazon - Ads and Organic",
            filters: [
              {
                breakdown: "Revenue Source (Northbeam)",
                value: "Amazon - Ads and Organic",
              },
            ],
          },
          {
            metricId: "rev",
            name: "Revenue: Online Store Revenue",
            filters: [
              {
                breakdown: "Revenue Source (Northbeam)",
                value: "Online Store Revenue",
              },
            ],
          },
        ],
      },
      {
        title: (
          <>
            {designElementManager.preset.getComponent()} Revenue
            <span className="text-sm text-v2-action mx-1">&</span>
            {designElementManager.tv.getComponent()} Video Spend
          </>
        ),
        metrics: [
          {
            metricId: "rev",
            name: "Revenue",
            filters: [],
          },
          {
            metricId: "spend",
            name: "Spend: Paid - Video",
            filters: [
              { breakdown: "Category (Northbeam)", value: "Paid - Video" },
            ],
          },
          {
            metricId: "spend",
            name: "Spend: Paid - TV",
            filters: [{ breakdown: "Category (Northbeam)", value: "TV" }],
          },
        ],
      },
      {
        title: (
          <>
            {designElementManager.facebook.getComponent()}
            Facebook Spend{" "}
            <span className="text-sm text-v2-action mx-1">&</span>
            {designElementManager.preset.getComponent()}
            Lower Funnel Activity
          </>
        ),
        metrics: [
          {
            metricId: "spend",
            name: "Facebook Spend",
            filters: [
              { breakdown: "Platform (Northbeam)", value: "Facebook Ads" },
            ],
          },
          {
            metricId: "visits",
            name: "Lower Funnel Organic Visits",
            filters: [
              { breakdown: "Category (Northbeam)", value: "Unattributed" },
              { breakdown: "Category (Northbeam)", value: "Organic" },
              { breakdown: "Category (Northbeam)", value: "Organic Search" },
              { breakdown: "Category (Northbeam)", value: "Organic Social" },
              { breakdown: "Category (Northbeam)", value: "Organic Shopping" },
            ],
          },
          {
            metricId: "rev",
            name: "Lower Funnel Revenue",
            filters: [
              { breakdown: "Category (Northbeam)", value: "Unattributed" },
              { breakdown: "Category (Northbeam)", value: "Organic" },
              { breakdown: "Category (Northbeam)", value: "Organic Search" },
              { breakdown: "Category (Northbeam)", value: "Organic Social" },
              { breakdown: "Category (Northbeam)", value: "Organic Shopping" },
            ],
          },
        ],
      },
      {
        title: (
          <>
            {designElementManager.preset.getComponent()} MER
            <span className="text-sm text-v2-action mx-1">&</span>
            {designElementManager.preset.getComponent()}
            Platform ROAS
          </>
        ),
        metrics: [
          {
            metricId: "roas",
            name: "D2C Cash MER",
            filters: [
              {
                breakdown: "Revenue Source (Northbeam)",
                value: "Online Store Revenue",
              },
            ],
            accountingMode: "cash",
          },
          ...topSpendPlatforms.map((platform) => ({
            metricId: "roas",
            name: `ROAS: ${platform}`,
            filters: [{ breakdown: "Platform (Northbeam)", value: platform }],
          })),
        ],
      },
      {
        title: (
          <>
            {designElementManager.preset.getComponent()} Spend
            <span className="text-sm text-v2-action mx-1">&</span>
            {designElementManager.organicSearch.getComponent()} Organic
            Transactions
          </>
        ),
        metrics: [
          {
            metricId: "spend",
            name: "All Spend",
            filters: [],
          },
          {
            metricId: "txns",
            name: "Organic Transactions",
            filters: [
              { breakdown: "Category (Northbeam)", value: "Unattributed" },
              { breakdown: "Category (Northbeam)", value: "Organic" },
              { breakdown: "Category (Northbeam)", value: "Organic Search" },
              { breakdown: "Category (Northbeam)", value: "Organic Social" },
              { breakdown: "Category (Northbeam)", value: "Organic Shopping" },
            ],
          },
        ],
      },
    ];

    if (platformLabels) {
      result.push({
        title: (
          <>{designElementManager.amazon.getComponent()} Amazon Halo Effect</>
        ),
        metrics: [
          {
            metricId: "spend",
            name: "Spend Excluding Amazon",
            filters: platformLabels.choices
              .filter((e) => !e.value.includes("Amazon"))
              .map((platform) => ({
                breakdown: "Platform (Northbeam)",
                value: platform.value,
              })),
          },
          {
            metricId: "rev",
            name: "Amazon revenue",
            filters: [
              {
                breakdown: "Revenue Source (Northbeam)",
                value: "Amazon - Ads and Organic",
              },
            ],
          },
        ],
      });
    }

    if (platformLabels?.choices.find((e) => e.value === "Podcast")) {
      result.push({
        title: (
          <>
            {designElementManager.influencers.getComponent()}
            Podcast Spend
            <span className="text-sm text-v2-action mx-1">&</span>
            {designElementManager.preset.getComponent()}
            Revenue by Source
          </>
        ),
        metrics: [
          {
            metricId: "spend",
            name: "Podcast Spend",
            filters: [
              {
                breakdown: "Platform (Northbeam)",
                value: "Podcast",
              },
            ],
          },
          {
            metricId: "rev",
            name: "Amazon - Ads and Organic Revenue",
            filters: [
              {
                breakdown: "Revenue Source (Northbeam)",
                value: "Amazon - Ads and Organic",
              },
            ],
          },
          {
            metricId: "rev",
            name: "Online Store Revenue",
            filters: [
              {
                breakdown: "Revenue Source (Northbeam)",
                value: "Online Store Revenue",
              },
            ],
          },
        ],
      });
    }

    return result;
  }, [topSpendPlatforms, breakdownConfigs]);
};
